import useWeather from "@/hooks/useWeather";
import useNow from "@/hooks/useNow";

/**
 * Golden-hour progress in the run-up to sunset.
 *
 * Reads the shared weather state for today's sunset time and returns
 * a normalized `progress` (0 → 1) across the configurable window that
 * ends at sunset. Consumers use `progress` to interpolate a colour or
 * effect so the UI subtly nods to the day winding down.
 *
 * Returns `{ active: false, progress: 0 }` when we're outside the
 * window, weather hasn't loaded, or location was denied — the caller
 * should just fall through to its default styling.
 */
export default function useGoldenHour({ windowMs = 30 * 60 * 1000 } = {}) {
  const { status, sunsetIso } = useWeather({ autoRequest: false });
  const now = useNow();
  if (status !== "ok" || !sunsetIso) return { active: false, progress: 0 };
  const sunset = new Date(sunsetIso).getTime();
  if (!Number.isFinite(sunset)) return { active: false, progress: 0 };
  const delta = sunset - now;
  // Outside the window (too early, or already past sunset).
  if (delta <= 0 || delta > windowMs) return { active: false, progress: 0 };
  return { active: true, progress: 1 - delta / windowMs };
}
